import * as React from 'react';
import { Link } from 'react-router';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { PlantsTableColumnHeader } from './plants-table-column-header.tsx';
import { PlantsTableRowActions } from './plants-table-row-actions.tsx';
import { PlantsTablePagination } from './plants-table-pagination.tsx';
import { PlantsWithOverview } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { ImageWithPlaceholder } from '../image-with-placeholder.tsx';
import PlaceholderPlantImage from '@/assets/placeholder-plant.jpg';
import { SolarPanelArrayIcon } from '@/assets/solar-panel-array-icon.tsx';
import { BatteryIcon } from '@/assets/battery-icon.tsx';
import type { ViewType } from '../plants-list-view-filter-command.tsx';
import { getImageUrl } from '@/features/plants/utils/get-image-url.ts';

const formatValueAndUnit = (inputNumber: number | undefined) => {
  if (inputNumber == null) {
    return { value: '--', unit: '' };
  }

  const isValueBelow1k = inputNumber < 1000;
  const value = isValueBelow1k ? inputNumber.toFixed(2) : (inputNumber / 1000).toFixed(2);
  const unit = isValueBelow1k ? 'kWh' : 'MWh';

  return { value, unit };
};

function getPlantStatusBadgeVariant(value: string | undefined) {
  switch (value) {
    case 'Operational':
      return 'green';
    case 'Planning':
      return 'blue';
    case 'Under Construction':
      return 'yellow';
    default:
      return 'gray';
  }
}

function getMqttStatusBadgeVariant(value: number | undefined) {
  switch (Number(value)) {
    case 1:
      return 'green';
    case 0:
      return 'red';
    case -1:
      return 'gray';
    default:
      return 'gray';
  }
}

function getMqttStatusLabel(mqttStatus: number | undefined): string {
  switch (mqttStatus) {
    case 1:
      return 'Connected';
    case 0:
      return 'Disconnected';
    case -1:
      return 'Unknown';
    default:
      return 'Unknown';
  }
}

function getBatteryStatusBadgeVariant(status: string | undefined) {
  const normalizedStatus = status?.toLowerCase();
  switch (normalizedStatus) {
    case 'charging':
      return 'green';
    case 'discharging':
      return 'yellow';
    case 'idle':
    case 'standby':
      return 'blue';
    case 'full':
      return 'purple';
    case 'empty':
    case 'low':
      return 'red';
    case 'error':
    case 'fault':
      return 'red';
    default:
      return 'gray';
  }
}

const setupTypeOrder: Record<string, number> = {
  'production-storage': 1,
  'production-only': 2,
  'storage-only': 3,
};

const plantStatusOrder: Record<string, number> = {
  Operational: 1,
  'Under Construction': 2,
  Planning: 3,
  Maintenance: 4,
};

const commonColumns: ColumnDef<PlantsWithOverview>[] = [
  {
    id: 'name',
    accessorKey: 'basicDetails.name',
    header: ({ column }) => <PlantsTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => {
      const plant = row.original;

      return (
        <div className="flex items-center gap-3">
          <Link className="h-10 w-10 shrink-0 overflow-hidden rounded-md" to={plant.plantId}>
            <ImageWithPlaceholder
              src={getImageUrl(plant.signedImageUrl)}
              alt={plant.basicDetails.name}
              loading="lazy"
              placeholderSrc={PlaceholderPlantImage}
              className="h-full w-full rounded-full object-cover"
            />
          </Link>
          <Link
            to={plant.plantId}
            className="max-w-[200px] truncate font-medium hover:underline"
          >
            {plant.basicDetails.name}
          </Link>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      return rowA.original.basicDetails.name.localeCompare(rowB.original.basicDetails.name);
    },
  },
  {
    size: 40,
    id: 'setupType',
    accessorKey: 'technicalSpecifications.setupType',
    header: ({ column }) => <PlantsTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => {
      const setupType = row.original.technicalSpecifications.setupType;
      return (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-gray-500">
            {setupType !== 'production-only' && <BatteryIcon width="14" />}
            {(setupType === 'production-storage' || setupType === 'production-only') && (
              <SolarPanelArrayIcon width="18" height="15" />
            )}
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const typeA = rowA.original.technicalSpecifications.setupType;
      const typeB = rowB.original.technicalSpecifications.setupType;
      return (setupTypeOrder[typeA] ?? 99) - (setupTypeOrder[typeB] ?? 99);
    },
  },
  {
    id: 'location',
    accessorKey: 'siteInformation.city.label',
    header: ({ column }) => <PlantsTableColumnHeader column={column} title="Location" />,
    cell: ({ row }) => {
      const plant = row.original;
      return (
        <span className="max-w-[150px] truncate text-sm text-gray-600">
          {plant.siteInformation.city?.label ?? plant.siteInformation.country?.label}
        </span>
      );
    },
    enableSorting: true,
    sortingFn: (rowA, rowB) => {
      const locationA =
        rowA.original.siteInformation.city?.label ??
        rowA.original.siteInformation.country?.label ??
        '';
      const locationB =
        rowB.original.siteInformation.city?.label ??
        rowB.original.siteInformation.country?.label ??
        '';
      return locationA.localeCompare(locationB);
    },
  },
  {
    id: 'status',
    accessorKey: 'basicDetails.status',
    header: ({ column }) => <PlantsTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = row.original.basicDetails.status;
      return <Badge variant={getPlantStatusBadgeVariant(status)}>{status}</Badge>;
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.original.basicDetails.status;
      const statusB = rowB.original.basicDetails.status;
      return (plantStatusOrder[statusA] ?? 99) - (plantStatusOrder[statusB] ?? 99);
    },
  },
  {
    id: 'mqttStatus',
    accessorKey: 'mqttStatus',
    header: ({ column }) => <PlantsTableColumnHeader column={column} title="Connection" />,
    cell: ({ row }) => {
      const mqttStatus = row.original.mqttStatus;
      return (
        <Badge variant={getMqttStatusBadgeVariant(mqttStatus)}>
          {getMqttStatusLabel(mqttStatus)}
        </Badge>
      );
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.original.mqttStatus ?? -2;
      const statusB = rowB.original.mqttStatus ?? -2;
      return statusB - statusA;
    },
  },
];

const productionColumns: ColumnDef<PlantsWithOverview>[] = [
  {
    id: 'yieldToday',
    accessorKey: 'yieldToday',
    header: ({ column }) => (
      <PlantsTableColumnHeader
        className="text-nowrap"
        column={column}
        title="Today production"
      />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.yieldToday);
      return (
        <span className="text-base text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.yieldToday ?? -1;
      const valueB = rowB.original.yieldToday ?? -1;
      return valueA - valueB;
    },
  },
  {
    id: 'totalYield',
    accessorKey: 'totalYield',
    header: ({ column }) => (
      <PlantsTableColumnHeader
        className="text-nowrap"
        column={column}
        title="YTD production"
      />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.totalYield);
      return (
        <span className="text-base text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.totalYield ?? -1;
      const valueB = rowB.original.totalYield ?? -1;
      return valueA - valueB;
    },
  },
  {
    id: 'consumptionToday',
    accessorKey: 'consumptionToday',
    header: ({ column }) => (
      <PlantsTableColumnHeader
        className="text-nowrap"
        column={column}
        title="Today consumption"
      />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.consumptionToday);
      return (
        <span className="text-base text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.consumptionToday ?? -1;
      const valueB = rowB.original.consumptionToday ?? -1;
      return valueA - valueB;
    },
  },
  {
    id: 'fedToGrid',
    accessorKey: 'fedToGrid',
    header: ({ column }) => (
      <PlantsTableColumnHeader className="text-nowrap" column={column} title="Fed to Grid" />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.fedToGrid);
      return (
        <span className="text-base text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.fedToGrid ?? -1;
      const valueB = rowB.original.fedToGrid ?? -1;
      return valueA - valueB;
    },
  },
];

const storageColumns: ColumnDef<PlantsWithOverview>[] = [
  {
    id: 'batteryStateOfCharge',
    accessorKey: 'batteryStateOfCharge',
    header: ({ column }) => (
      <PlantsTableColumnHeader className="text-nowrap" column={column} title="SoC" />
    ),
    cell: ({ row }) => {
      const soc = row.original.batteryStateOfCharge;
      const hasSoc = soc != null;
      return (
        <span className="text-base text-nowrap">
          {hasSoc ? soc : '--'} {hasSoc && <span className="text-sm text-gray-500">%</span>}
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.batteryStateOfCharge ?? -1;
      const valueB = rowB.original.batteryStateOfCharge ?? -1;
      return valueA - valueB;
    },
  },
  {
    id: 'batteryStatus',
    accessorKey: 'batteryStatus',
    header: ({ column }) => (
      <PlantsTableColumnHeader
        className="text-nowrap"
        column={column}
        title="Battery Status"
      />
    ),
    cell: ({ row }) => {
      const status = row.original.batteryStatus;
      if (!status) return <span className="text-base text-gray-400">--</span>;
      return <Badge variant={getBatteryStatusBadgeVariant(status)}>{status}</Badge>;
    },
    sortingFn: (rowA, rowB) => {
      const statusA = rowA.original.batteryStatus ?? '';
      const statusB = rowB.original.batteryStatus ?? '';
      return statusA.localeCompare(statusB);
    },
  },
  {
    id: 'batteryChargePowerToday',
    accessorKey: 'batteryChargePowerToday',
    header: ({ column }) => (
      <PlantsTableColumnHeader className="text-nowrap" column={column} title="Today Charge" />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.batteryChargePowerToday);
      return (
        <span className="text-base text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.batteryChargePowerToday ?? -1;
      const valueB = rowB.original.batteryChargePowerToday ?? -1;
      return valueA - valueB;
    },
  },
  {
    id: 'batteryDischargePowerToday',
    accessorKey: 'batteryDischargePowerToday',
    header: ({ column }) => (
      <PlantsTableColumnHeader
        className="text-nowrap"
        column={column}
        title="Today Discharge"
      />
    ),
    cell: ({ row }) => {
      const { value, unit } = formatValueAndUnit(row.original.batteryDischargePowerToday);
      return (
        <span className="text-sm text-nowrap">
          {value} <span className="text-sm text-gray-500">{unit}</span>
        </span>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.batteryDischargePowerToday ?? -1;
      const valueB = rowB.original.batteryDischargePowerToday ?? -1;
      return valueA - valueB;
    },
  },
];

const actionsColumn: ColumnDef<PlantsWithOverview> = {
  size: 10,
  id: 'actions',
  cell: ({ row }) => <PlantsTableRowActions row={row} />,
  enablePinning: true,
};

function getColumns(viewType: ViewType): ColumnDef<PlantsWithOverview>[] {
  if (viewType === 'storage') {
    return [...commonColumns, ...storageColumns, actionsColumn];
  }

  if (viewType === 'production') {
    return [...commonColumns, ...productionColumns, actionsColumn];
  }

  return [...commonColumns, ...productionColumns, actionsColumn];
}

export function PlantsTable({
  data,
  isLoading,
  viewType,
}: {
  data: PlantsWithOverview[];
  isLoading: boolean;
  viewType: ViewType;
}) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const columns = React.useMemo(() => getColumns(viewType), [viewType]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageSize: 20,
      },
      columnPinning: {
        right: ['actions'],
      },
    },
    state: {
      sorting,
    },
    enableColumnPinning: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-white">
        <div className="relative w-full overflow-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          width: header.getSize() !== 150 ? header.getSize() : undefined,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="group transition-none duration-0"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`px-2 py-3 ${
                          cell.column.id === 'actions' ? 'bg-white group-hover:bg-gray-50' : ''
                        }`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    {isLoading ? (
                      <div className="flex w-full items-center justify-center py-20">
                        <LoadingSpinner size={50} />
                      </div>
                    ) : (
                      'No plants found'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <PlantsTablePagination table={table} />
    </div>
  );
}
